//#region frontend/lib/quiz-analytics.js
var ENDPOINT = "https://happy-howl-app.vercel.app/api/quiz-events";
/** Session ids live per quiz ATTEMPT, so completion rate is a ratio of attempts, not of people. */
var SESSION_KEY = "quiz_session_id";
function uuid() {
	try {
		if (crypto?.randomUUID) return crypto.randomUUID();
	} catch {}
	return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
function readSession() {
	try {
		return sessionStorage.getItem(SESSION_KEY);
	} catch {
		return null;
	}
}
function writeSession(id) {
	try {
		sessionStorage.setItem(SESSION_KEY, id);
	} catch {}
}
/**
* Fire and forget.
*
* sendBeacon, not fetch, because the event that matters most is the one sent as the page goes away:
* a bounce IS an unload, and a fetch() started during unload is cancelled. The Blob is typed
* text/plain so the request stays a CORS "simple request" with no preflight — an OPTIONS round trip
* would not survive unload either. The collector parses text, not JSON, for exactly this reason.
*/
function send(event) {
	try {
		const body = JSON.stringify({ events: [event] });
		if (navigator.sendBeacon) {
			if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "text/plain" }))) return;
		}
		fetch(ENDPOINT, {
			method: "POST",
			body,
			headers: { "Content-Type": "text/plain" },
			keepalive: true
		}).catch(() => {});
	} catch {}
}
/**
* One tracker per quiz instance. Owns the attempt's session id and which steps it has already
* reported, so the caller can fire freely without thinking about duplicates.
*
* @param {'standalone' | 'pdp' | 'calculator'} source Must match the `_quiz_source` values stamped
*   on order lines, since the warehouse joins the funnel to revenue on this column.
* @param {{ entryMode?: 'form' | 'deep_link' }} [options] How this attempt began. Set by the
*   calculator, which can be linked to with the answers already in the query string
*   (?name=&weight=&condition=) and then builds the guide on load. Those attempts complete without
*   anyone answering a question, so without this they would sit in the same bucket as worked-through
*   attempts and read as a jump in completion rate. Describes how the attempt STARTED: a shopper who
*   deep-links and then re-runs it by hand stays `deep_link`, since start() is idempotent and the
*   attempt is still the one the link began.
*/
function createQuizTracker(source, { entryMode } = {}) {
	let sessionId = readSession();
	let started = false;
	const seenSteps = /* @__PURE__ */ new Set();
	function emit(eventName, fields = {}) {
		try {
			if (!sessionId) return;
			send({
				event_id: uuid(),
				session_id: sessionId,
				event_name: eventName,
				quiz_source: source,
				...entryMode && { entry_mode: entryMode },
				page_path: location.pathname,
				occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
				...fields
			});
		} catch {}
	}
	return {
		sessionId() {
			try {
				return sessionId || readSession();
			} catch {
				return null;
			}
		},
		start() {
			try {
				if (started) return;
				started = true;
				sessionId = uuid();
				writeSession(sessionId);
				seenSteps.clear();
				emit("quiz_start");
			} catch {}
		},
		stepView(index, name) {
			try {
				if (seenSteps.has(index)) return;
				seenSteps.add(index);
				emit("quiz_step_view", {
					step_index: index,
					step_name: name
				});
			} catch {}
		},
		stepAnswer(index, name, value) {
			try {
				if (!name || name === "intro") return;
				if (value === void 0 || value === null || value === "") return;
				emit("quiz_step_answer", {
					step_index: index,
					step_name: name,
					step_value: String(value)
				});
			} catch {}
		},
		stepBack(index, name) {
			emit("quiz_step_back", {
				step_index: index,
				step_name: name
			});
		},
		complete({ weight, mealSize, conditionCode, variantId, variantTitle, sellingPlanId, sellingPlanName } = {}) {
			emit("quiz_complete", {
				weight,
				meal_size: mealSize,
				condition_code: conditionCode,
				variant_id: variantId,
				variant_title: variantTitle,
				selling_plan_id: sellingPlanId,
				selling_plan_name: sellingPlanName
			});
		},
		error(type) {
			emit("quiz_error", { error_type: type });
		}
	};
}
//#endregion
export { createQuizTracker as t };
